import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchInsulinMeasurements,
  withD1NAMOErrorHandling,
} from "../services/api-config";
import type {
  InsulinMeasurement,
  InsulinMeasurementResponse,
} from "../types/api";

/**
 * Hook for fetching insulin measurements for a patient
 * Provides comprehensive data management with caching, error handling, and optimistic updates
 */
export function useInsulinData(
  patientId?: string,
  options?: {
    startDate?: string;
    endDate?: string;
    type?: string;
    enabled?: boolean;
  },
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      "insulin-data",
      patientId,
      options?.startDate,
      options?.endDate,
      options?.type,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for insulin data");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchInsulinMeasurements(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            type: options?.type,
          }),
        {
          endpoint: "/insulin",
          patientId,
          operation: "fetch insulin measurements",
        },
      );
    },
    enabled: Boolean(patientId) && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors, but retry up to 3 times on 5xx errors
      if (error && typeof error === "object" && "status" in error) {
        const status = error.status as number;
        return status >= 500 && failureCount < 3;
      }
      return failureCount < 2;
    },
  });

  /**
   * Mutation for adding new insulin measurements
   */
  const addInsulinMeasurement = useMutation({
    mutationFn: async (
      measurement: Omit<InsulinMeasurement, "id" | "createdAt">,
    ) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      const response = await fetch("/api/insulin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({
          ...measurement,
          patientId,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to add insulin measurement: ${response.statusText}`,
        );
      }

      return response.json() as Promise<InsulinMeasurement>;
    },
    onMutate: async (newMeasurement) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["insulin-data", patientId],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
      );

      // Optimistically update to the new value
      const optimisticMeasurement: InsulinMeasurement = {
        ...newMeasurement,
        id: `temp-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: [...(old.data || []), optimisticMeasurement],
            metadata: {
              ...old.metadata,
              count: (old.metadata?.count || 0) + 1,
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _newMeasurement, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(
          ["insulin-data", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ["insulin-data", patientId] });
    },
  });

  /**
   * Mutation for updating existing insulin measurements
   */
  const updateInsulinMeasurement = useMutation({
    mutationFn: async (measurement: InsulinMeasurement) => {
      const response = await fetch(`/api/insulin/${measurement.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(measurement),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to update insulin measurement: ${response.statusText}`,
        );
      }

      return response.json() as Promise<InsulinMeasurement>;
    },
    onMutate: async (updatedMeasurement) => {
      await queryClient.cancelQueries({
        queryKey: ["insulin-data", patientId],
      });

      const previousData = queryClient.getQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
      );

      queryClient.setQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((measurement: any) =>
              measurement.id === updatedMeasurement.id
                ? updatedMeasurement
                : measurement,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _updatedMeasurement, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["insulin-data", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["insulin-data", patientId] });
    },
  });

  /**
   * Mutation for deleting insulin measurements
   */
  const deleteInsulinMeasurement = useMutation({
    mutationFn: async (measurementId: string) => {
      const response = await fetch(`/api/insulin/${measurementId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to delete insulin measurement: ${response.statusText}`,
        );
      }

      return measurementId;
    },
    onMutate: async (measurementId) => {
      await queryClient.cancelQueries({
        queryKey: ["insulin-data", patientId],
      });

      const previousData = queryClient.getQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
      );

      queryClient.setQueryData<InsulinMeasurementResponse>(
        ["insulin-data", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(
              (measurement: any) => measurement.id !== measurementId,
            ),
            metadata: {
              ...old.metadata,
              count: Math.max((old.metadata?.count || 0) - 1, 0),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _measurementId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["insulin-data", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["insulin-data", patientId] });
    },
  });

  /**
   * Utility function to refetch insulin data
   */
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["insulin-data", patientId] });
  };

  /**
   * Utility function to get insulin data summary
   */
  const getInsulinSummary = () => {
    if (!query.data?.data) return null;

    const measurements = query.data.data;
    const totalUnits = measurements.reduce((sum, m) => sum + m.units, 0);
    const rapidUnits = measurements
      .filter((m) => m.type === "rapid")
      .reduce((sum, m) => sum + m.units, 0);
    const longActingUnits = measurements
      .filter((m) => m.type === "long-acting")
      .reduce((sum, m) => sum + m.units, 0);

    return {
      totalMeasurements: measurements.length,
      totalUnits,
      rapidUnits,
      longActingUnits,
      averageUnitsPerDay:
        measurements.length > 0 ? totalUnits / measurements.length : 0,
      mostRecentMeasurement: measurements.sort(
        (a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
      )[0],
    };
  };

  return {
    // Query state
    data: query.data?.data || [],
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,

    // Mutations
    addInsulinMeasurement: addInsulinMeasurement.mutate,
    updateInsulinMeasurement: updateInsulinMeasurement.mutate,
    deleteInsulinMeasurement: deleteInsulinMeasurement.mutate,

    // Mutation states
    isAdding: addInsulinMeasurement.isPending,
    isUpdating: updateInsulinMeasurement.isPending,
    isDeleting: deleteInsulinMeasurement.isPending,

    // Utilities
    refetch,
    getInsulinSummary,
  };
}

/**
 * Hook for fetching insulin data with pagination support
 */
export function useInsulinDataPaginated(
  patientId?: string,
  options?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    type?: string;
  },
) {
  const { page = 1, limit = 50 } = options || {};

  return useQuery({
    queryKey: [
      "insulin-data-paginated",
      patientId,
      page,
      limit,
      options?.startDate,
      options?.endDate,
      options?.type,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for insulin data");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchInsulinMeasurements(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            type: options?.type,
          }),
        {
          endpoint: "/insulin",
          patientId,
          operation: "fetch paginated insulin measurements",
        },
      );
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = data.data?.slice(startIndex, endIndex) || [];

      return {
        data: paginatedData,
        metadata: {
          ...data.metadata,
          currentPage: page,
          totalPages: Math.ceil((data.metadata?.count || 0) / limit),
          hasNextPage: endIndex < (data.metadata?.count || 0),
          hasPreviousPage: page > 1,
        },
      };
    },
  });
}
